import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Building, Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function BankAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ bank_name: '', account_number: '', account_holder: '', is_active: true });

  const load = () => api.get('/tenant/bank-accounts').then(r => { setAccounts(r.data.accounts); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ bank_name: '', account_number: '', account_holder: '', is_active: true }); setShowForm(false); setEditing(null); };

  const handleSave = async () => {
    if (!form.bank_name || !form.account_number || !form.account_holder) { toast.error('Lengkapi semua field'); return; }
    try {
      if (editing) {
        await api.put(`/tenant/bank-accounts/${editing}`, form);
        toast.success('Rekening diupdate');
      } else {
        await api.post('/tenant/bank-accounts', form);
        toast.success('Rekening ditambahkan');
      }
      resetForm();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus rekening ini?')) return;
    try { await api.delete(`/tenant/bank-accounts/${id}`); toast.success('Dihapus'); load(); }
    catch (e) { toast.error('Gagal menghapus'); }
  };

  const startEdit = (a) => { setForm({ bank_name: a.bank_name, account_number: a.account_number, account_holder: a.account_holder, is_active: a.is_active }); setEditing(a.id); setShowForm(true); };

  return (
    <div data-testid="bank-accounts-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>Rekening Bank</h1>
        <Button onClick={() => { resetForm(); setShowForm(true); }}><Plus size={16} className="mr-2" /> Tambah Rekening</Button>
      </div>

      {showForm && (
        <Card className="mb-6 border-blue-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{editing ? 'Edit Rekening' : 'Tambah Rekening Baru'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Nama Bank</Label>
                <Input placeholder="BCA, BNI, BRI, Mandiri..." value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} />
              </div>
              <div>
                <Label>Nomor Rekening</Label>
                <Input placeholder="1234567890" value={form.account_number} onChange={e => setForm({ ...form, account_number: e.target.value })} />
              </div>
              <div>
                <Label>Atas Nama</Label>
                <Input placeholder="Nama pemilik rekening" value={form.account_holder} onChange={e => setForm({ ...form, account_holder: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded" />
              Aktif (tampilkan di halaman checkout)
            </label>
            <div className="flex gap-2">
              <Button onClick={handleSave}><Check size={16} className="mr-1" /> Simpan</Button>
              <Button variant="outline" onClick={resetForm}><X size={16} className="mr-1" /> Batal</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-400">Memuat...</div>
      ) : accounts.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="py-12 text-center text-slate-400">
          <Building size={48} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">Belum ada rekening bank</p>
          <p className="text-sm mt-1">Tambahkan rekening bank agar pembeli bisa transfer manual</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map(a => (
            <Card key={a.id} className="border-slate-200 shadow-sm">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Building size={20} className="text-blue-600" /></div>
                    <div>
                      <p className="font-bold text-sm">{a.bank_name}</p>
                      <Badge variant="outline" className={a.is_active ? 'bg-emerald-50 text-emerald-700 text-xs' : 'bg-slate-100 text-slate-500 text-xs'}>{a.is_active ? 'Aktif' : 'Nonaktif'}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(a)} className="p-1.5 rounded hover:bg-slate-100"><Pencil size={14} className="text-slate-400" /></button>
                    <button onClick={() => handleDelete(a.id)} className="p-1.5 rounded hover:bg-red-50"><Trash2 size={14} className="text-red-400" /></button>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  <p className="font-mono text-lg tracking-wider text-slate-800">{a.account_number}</p>
                  <p className="text-slate-500">a.n. <span className="font-medium text-slate-700">{a.account_holder}</span></p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
